import { useCallback, useEffect, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { Star, MessageSquareHeart, Sparkles, ShieldCheck, MessageCircle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import ReviewSubmitDialog from "@/components/ReviewSubmitDialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const PERSONA_LABEL = {
  doctor: "Doctor", nurse: "Nurse", pilot: "Pilot", steward: "Cabin Crew",
  train_driver: "Train Driver", security: "Security", courier: "Courier",
  traveler: "Traveler", shift_worker: "Shift Worker",
};

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const d = Math.floor(ms / 86_400_000);
  if (d <= 0) return "today";
  if (d === 1) return "yesterday";
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
}

export default function ReviewsSection({ openSignal }) {
  const [data, setData] = useState({ items: [], count: 0, avg_rating: 0 });
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/reviews");
      setData(data);
    } catch { /* empty state below */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // External trigger (e.g., from badge unlock milestone)
  useEffect(() => {
    if (openSignal) setOpen(true);
  }, [openSignal]);

  const items = data.items || [];

  return (
    <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-14" data-testid="reviews-section">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="pill text-indigo-300 border-indigo-500/30 bg-indigo-950/60 mb-3">
            <MessageSquareHeart className="w-3.5 h-3.5" /> Loved by shift workers
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
            What clients say
          </h2>
          {data.count > 0 && (
            <div className="flex items-center gap-2 mt-2 text-slate-400 text-sm">
              <div className="flex items-center gap-0.5">
                {[1,2,3,4,5].map((n) => (
                  <Star
                    key={n}
                    className={`w-4 h-4 ${
                      n <= Math.round(data.avg_rating)
                        ? "text-amber-300 fill-amber-300"
                        : "text-slate-700"
                    }`}
                  />
                ))}
              </div>
              <span className="font-mono-ui">{data.avg_rating} · {data.count} reviews</span>
            </div>
          )}
        </div>
        <Button
          onClick={() => setOpen(true)}
          data-testid="btn-open-review-submit"
          className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white glow-indigo"
        >
          <Sparkles className="w-4 h-4 mr-1" /> Leave a review
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="circa-card p-10 text-center">
          <MessageSquareHeart className="w-8 h-8 text-slate-500 mx-auto mb-3" />
          <div className="font-display text-lg font-semibold text-slate-200 mb-1">Be the first to share your story</div>
          <div className="text-slate-400 text-sm">Reviews from doctors, pilots, nurses, and drivers appear here.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.slice(0, 9).map((r) => (
            <ReviewCard key={r.id} review={r} onChanged={load} />
          ))}
        </div>
      )}

      <ReviewSubmitDialog
        open={open}
        onOpenChange={setOpen}
        onSubmitted={load}
      />
    </section>
  );
}

function ReviewCard({ review: r, onChanged }) {
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";
  const [replying, setReplying] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const submitReply = async () => {
    if (text.trim().length < 1) return;
    setBusy(true);
    try {
      await http.put(`/reviews/${r.id}/reply`, { reply: text.trim() });
      toast.success("Reply posted");
      setReplying(false);
      setText("");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't post reply");
    } finally { setBusy(false); }
  };

  const removeReply = async () => {
    setBusy(true);
    try {
      await http.delete(`/reviews/${r.id}/reply`);
      toast.success("Reply removed");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete reply");
    } finally { setBusy(false); }
  };

  return (
    <div className="circa-card p-6 flex flex-col" data-testid={`review-card-${r.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-0.5">
          {[1,2,3,4,5].map((n) => (
            <Star
              key={n}
              className={`w-4 h-4 ${n <= r.rating ? "text-amber-300 fill-amber-300" : "text-slate-700"}`}
            />
          ))}
        </div>
        {r.verified && (
          <span
            className="pill bg-emerald-950/60 border-emerald-500/40 text-emerald-300 text-[9px]"
            data-testid={`review-verified-${r.id}`}
          >
            <ShieldCheck className="w-3 h-3" /> Verified user
          </span>
        )}
      </div>

      <p className="text-slate-200 text-sm leading-relaxed flex-1">"{r.comment}"</p>

      <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-100">{r.name}</div>
          <div className="text-[10px] uppercase tracking-widest font-mono-ui text-slate-500">
            {PERSONA_LABEL[r.persona] || r.persona}
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-widest font-mono-ui text-slate-500">
          {timeAgo(r.created_at)}
        </div>
      </div>

      {/* Founder reply */}
      {r.reply && (
        <div className="mt-4 rounded-xl border border-indigo-500/30 bg-indigo-500/10 p-3" data-testid={`review-reply-${r.id}`}>
          <div className="flex items-center gap-1.5 mb-1">
            <MessageCircle className="w-3 h-3 text-indigo-300" />
            <span className="text-[10px] uppercase tracking-widest font-mono-ui text-indigo-200">
              {r.reply_by || "Circa Team"} · reply
            </span>
            {isAdmin && (
              <button
                onClick={removeReply}
                disabled={busy}
                data-testid={`btn-delete-reply-${r.id}`}
                className="ml-auto text-slate-500 hover:text-rose-300 transition"
                aria-label="Remove reply"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
          <p className="text-slate-200 text-sm leading-relaxed">{r.reply}</p>
        </div>
      )}

      {/* Admin controls */}
      {isAdmin && !r.reply && !replying && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setReplying(true)}
          data-testid={`btn-reply-${r.id}`}
          className="mt-3 self-start rounded-full text-indigo-300 hover:text-indigo-200 hover:bg-indigo-500/10"
        >
          <MessageCircle className="w-3.5 h-3.5 mr-1" /> Reply as founder
        </Button>
      )}

      {isAdmin && replying && (
        <div className="mt-3 space-y-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="One line reply — keep it warm."
            rows={2}
            maxLength={280}
            data-testid={`reply-input-${r.id}`}
            className="bg-slate-900/60 border-white/10 text-slate-100 resize-none text-sm"
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => { setReplying(false); setText(""); }} className="rounded-full text-slate-400 hover:text-slate-200 hover:bg-white/5">
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={submitReply}
              disabled={busy || text.trim().length < 1}
              data-testid={`btn-submit-reply-${r.id}`}
              className="rounded-full bg-indigo-500 hover:bg-indigo-400 text-white"
            >
              Post reply
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
